import { type VoidFunctionComponent } from 'react';
import IssuesItem from './ProjectSubnavItemIssues';
import AuthedItems from './ProjectSubnavAuthedItems';
import useActiveKey from './useActiveKey';
import Subnav from '../../generic/Subnav';
import { useRevision } from '../../../hooks/api';
import { isAuthed, isProjectInCurrentOrg } from '../../../utils';

type Props = {
  /**
   * The organization which the project belongs to. This is used to hide
   * certain subnav items on public projects if the user has "view any project"
   * permissions, but belongs to another org. They get to see the "public" view
   * of the project, but shouldn't be allowed to see settings (for example).
   *
   * @example 17
   */
  projectOrgId: number;
  /**
   * This is the URL without the branch/revision part, which is needed for the Activity/Settings routes.
   * @example `/projects/custom%2B17%2Fcool-project`
   */
  baseProjectURL: string;
  /**
   * This is the full URL with the branch/revision part.
   * @example `/projects/custom%2B17%2Fcool-project/refs/branch/main/rev00001`
   */
  baseRevisionURL: string;
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
  /**
   * The revision ID
   * @example `rev00001`
   */
  revisionId: string | null;
};

/**
 * ProjectSubnav is the Subnav when viewing a project.
 * For SubnavItems which display a count, we have broken them
 * into their own components which can be responsible for fetching their own data.
 */
const ProjectSubnav: VoidFunctionComponent<Props> = ({
  projectOrgId,
  baseProjectURL,
  baseRevisionURL,
  projectLocator,
  revisionId,
}) => {
  //  There may not be a `revisionId` selected yet, so this waits to fetch it until there is a `revisionId`
  const shouldFetchRevision = !!revisionId;
  const { isLoading, data: revision } = useRevision(
    `${projectLocator}$${revisionId}`,
    shouldFetchRevision,
  );
  const activeKey = useActiveKey(baseProjectURL, baseRevisionURL);
  const isCurrentOrgProject = isProjectInCurrentOrg(projectOrgId);

  return (
    <Subnav activeKey={activeKey}>
      <Subnav.Item to={baseRevisionURL} navKey="summary">
        Summary
      </Subnav.Item>
      {isCurrentOrgProject && (
        <IssuesItem
          baseRevisionURL={baseRevisionURL}
          projectLocator={projectLocator}
          revisionScanId={revision?.latestRevisionScanId ?? undefined}
          revisionId={revisionId}
        />
      )}
      <Subnav.ItemWithCount
        to={`${baseRevisionURL}/browse/dependencies`}
        navKey="browse/dependencies"
        count={revision?.dependency_count ?? 0}
        isLoading={!revisionId || isLoading}
      >
        Dependencies
      </Subnav.ItemWithCount>
      <Subnav.ItemWithCount
        to={`${baseRevisionURL}/browse/licenses`}
        navKey="browse/licenses"
        count={revision?.license_count ?? 0}
        isLoading={!revisionId || isLoading}
      >
        Licenses
      </Subnav.ItemWithCount>
      {isAuthed() ? (
        <AuthedItems
          isCurrentOrgProject={isCurrentOrgProject}
          baseRevisionURL={baseRevisionURL}
          baseProjectURL={baseProjectURL}
        />
      ) : (
        <Subnav.Item to={`${baseProjectURL}/see-more`} navKey="see-more">
          See More
        </Subnav.Item>
      )}
    </Subnav>
  );
};

export default ProjectSubnav;
